// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import type { RootState } from "../../../app/store"
import { prepareListDaySummaryRequest } from "./daySummaries"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  ClearTimeGroupErrorsRequestSchema,
  ClearTimeGroupErrorsResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type ClearTimeGroupsErrorRpc = (request: Init<typeof ClearTimeGroupErrorsRequestSchema>) => Observable<ClearTimeGroupErrorsResponse>

export const clearTimeGroupErrors = (clearTimeGroupsErrorRpc: ClearTimeGroupsErrorRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.clearTimeGroupErrors.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => clearTimeGroupsErrorRpc(action.payload)
        .pipe(
          mergeMap(_ => concat(
            of(TimelineSlice.clearTimeGroupsPostErrors(action.payload.timeGroupIds ?? [])),
            of(Actions.listDaySummaries(prepareListDaySummaryRequest(state.timeline.activeDate.toString())))
          )),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_clear_time_rows_error_failed_precondition", message: "Failed to clear errors. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_clear_time_rows_error_permission_denied", message: "Failed to clear errors. You do not have permission to group the selected time rows." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_clear_time_rows_error_failed", message: "Failed to clear errors. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}
const clearTimeGroupsErrorRpc = (request: Init<typeof ClearTimeGroupErrorsRequestSchema>): Observable<ClearTimeGroupErrorsResponse> =>
  executorMutation(() => timelineService.clearTimeGroupErrors(create(ClearTimeGroupErrorsRequestSchema, request)))

export default clearTimeGroupErrors(clearTimeGroupsErrorRpc)
